import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import {
  fetchEodFetchStatus,
  fetchIntradayFetchStatus,
  selectors,
} from 'state';
import type { IntradayFetchSnapshot } from '../../../../state/app/admin/types';

let requestId = 0;

export function useIntradayStatus(active: boolean) {
  return usePriceFetchStatus(active, 'intraday');
}

export function useEodStatus(active: boolean) {
  return usePriceFetchStatus(active, 'eod');
}

function usePriceFetchStatus(active: boolean, mode: 'intraday' | 'eod') {
  const dispatch = useDispatch();
  const status = useSelector(
    mode === 'intraday'
      ? selectors.intradayFetchStatus
      : selectors.eodFetchStatus,
  );
  const fetchStatus =
    mode === 'intraday' ? fetchIntradayFetchStatus : fetchEodFetchStatus;
  const loading = useRef(status.loading);
  loading.current = status.loading;
  const lastRequested = useRef(0);
  const [now, setNow] = useState(Date.now);

  const refresh = useCallback(
    (invalidate = false) => {
      setNow(Date.now());
      if (
        !invalidate &&
        (document.visibilityState !== 'visible' ||
          loading.current ||
          Date.now() - lastRequested.current < 5000)
      )
        return;
      loading.current = true;
      lastRequested.current = Date.now();
      dispatch(
        fetchStatus({
          request_id: ++requestId,
          ...(invalidate ? { invalidate: true } : {}),
        }),
      );
    },
    [dispatch, fetchStatus],
  );
  // A save supersedes any request started with the previous tier configuration.
  const refreshAfterTierSave = useCallback(() => refresh(true), [refresh]);

  useEffect(() => {
    if (!active) return;
    const poll = () => refresh();
    poll();
    const timer = window.setInterval(poll, 30000);
    window.addEventListener('focus', poll);
    document.addEventListener('visibilitychange', poll);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', poll);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [active, refresh]);

  const stale = Boolean(
    status.snapshot &&
      (status.error ||
        status.snapshot.stale ||
        now - Date.parse(status.snapshot.computed_at) > 90000),
  );
  return { ...status, stale, refreshAfterTierSave };
}

export function IntradayIndicator({
  assetId,
  snapshot,
  mode = 'intraday',
}: {
  assetId: number;
  snapshot?: IntradayFetchSnapshot;
  mode?: 'intraday' | 'eod';
}) {
  if (!snapshot) return <span>—</span>;
  const reasons = snapshot.assets[assetId];
  if (!reasons) return <span>Off</span>;
  const explanation = [
    ...reasons.tier_ids.map(id => snapshot.tiers[id] || `Tier ${id}`),
    reasons.playing_session_ids.length
      ? `Playing real-time sessions: ${reasons.playing_session_ids.join(', ')}`
      : '',
    reasons.paused_session_ids.length
      ? `Paused real-time sessions: ${reasons.paused_session_ids.join(', ')}`
      : '',
    reasons.benchmark_playing_session_ids?.length
      ? `Performance benchmarks in playing real-time sessions: ${reasons.benchmark_playing_session_ids.join(', ')}`
      : '',
    reasons.benchmark_paused_session_ids?.length
      ? `Performance benchmarks in paused real-time sessions: ${reasons.benchmark_paused_session_ids.join(', ')}`
      : '',
    reasons.required_by_forward_ids?.length
      ? `Required by FX forward ${reasons.required_by_forward_ids.map(id => snapshot.forwards?.[id] || id).join(', ')}`
      : '',
    reasons.capture_route === 'intraday'
      ? 'Daily capture from the intraday stream'
      : '',
  ]
    .filter(Boolean)
    .join('; ');
  return (
    <Tooltip title={explanation}>
      <span
        tabIndex={0}
        aria-label={`${mode === 'intraday' ? 'Intraday' : 'EoD'} On: ${explanation}`}
      >
        On
      </span>
    </Tooltip>
  );
}
